/**
 * src/lib/toast.js — the app had no toast. Card P219 item 8 needs one.
 *
 * ⛔⛔ BUILT, NOT REUSED, AND THAT WAS CHECKED FIRST. Nothing in src/components
 * or src/lib exports a toast, a snackbar or a notification host (measured
 * 1 Nov). Writing a second one would have been the repo's recurring mistake;
 * there was no first one.
 *
 * ⚠ A STORE, NOT A COMPONENT. The toast must outlive the dialog that raised
 * it — the whole point of item 8 is that Carlos can close the report dialog,
 * keep working, and still be told when the brief is ready. A component-owned
 * toast dies with its owner.
 *
 * ⚠ TOASTS ARE KEYED. "Your report is being generated" must become "Your
 * report is ready" IN PLACE, not stack beneath itself. An unkeyed toast
 * system would have shown Carlos three notifications for one report.
 */

const listeners = new Set();
let toasts = [];

function emit() {
  const snapshot = toasts;
  for (const fn of [...listeners]) {
    try { fn(snapshot); } catch (_) { /* one bad subscriber must not stop the rest */ }
  }
}

export function subscribeToasts(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  fn(toasts);
  return () => { listeners.delete(fn); };
}

export function getToasts() { return toasts; }

/**
 * Raise or replace a toast.
 *
 * @param {object} t
 *   key      — identity; the same key REPLACES rather than stacks
 *   tone     — 'working' | 'success' | 'error'
 *   title    — one line
 *   body     — optional second line
 *   actions  — [{ label, onClick, testId }]
 *   sticky   — stays until dismissed or replaced (working + error default true)
 */
export function pushToast(t) {
  if (!t || !t.key) return;
  const tone = t.tone || 'working';
  const next = {
    sticky: tone !== 'success',
    ...t,
    tone,
    at: Date.now(),
  };
  const i = toasts.findIndex((x) => x.key === next.key);
  toasts = i === -1 ? [...toasts, next] : toasts.map((x, k) => (k === i ? next : x));
  emit();
}

export function dismissToast(key) {
  const before = toasts.length;
  toasts = toasts.filter((x) => x.key !== key);
  if (toasts.length !== before) emit();
}

export function __resetToastsForTests() {
  listeners.clear();
  toasts = [];
}
